import { useMemo } from 'react';
import { useCamera } from '../../hooks/cameraStore';
import styles from '../../styles/Battlefield.module.css';
import BattlefieldReadout from '../BattlefieldReadout';
import BattlefieldLegend from '../BattlefieldLegend';
import BattlefieldSceneBar from '../BattlefieldSceneBar';
import BattlefieldDeployBar from '../BattlefieldDeployBar';
import DisplayInstruments from '../display/DisplayInstruments';
import LastTurnScrub from '../display/LastTurnScrub';
import displayStyles from '../../styles/Display.module.css';
import { inspectAt, NOTHING_HERE } from '../../engine/battlefield/inspect';
import { teamsIn } from '../../engine/roster/team';
import { capacityWarningOf } from './panelGeometry';
import { legendKeyOf } from './legendKey';
import { levelWord } from '../../engine/display/coverScreens';

/**
 * WHAT THE CLIMB IS COSTING, in the same voice as the material facts under
 * it - `Rockcrete · hardness 8` and `Climbing · 1.5 m per metre` are the
 * same kind of statement. ONLY WHILE A ROUTE ACTUALLY CLIMBS: it sat in
 * the toolbar as a permanent line for one gesture in twenty (v8.12.1).
 */
function ClimbBar({ facts }) {
  if (!facts) return null;
  return (
    <div className={styles.inspectBar} data-testid="battlefield-climb-rate">
      <span className={styles.inspectEntry}>
        <b className={styles.inspectTitle}>Climbing</b>
        <span className={styles.inspectFacts}>{facts.join(' · ')}</span>
      </span>
    </div>
  );
}

/**
 * What the cursor is over. BUILD MODE IS WHERE IT IS WANTED MOST - "what
 * did I just paint" is exactly the question a GM laying a map has. A
 * hovered face names its solid, and that solid's own footprint is where
 * to ask: the only point guaranteed to be under what the pointer is over.
 */
function inspectedAt(hoverCell, { solids, zones, tile, isGm, hoverDetail }) {
  const hovered = hoverCell.solidId ? solids.find((s) => s.id === hoverCell.solidId) : null;
  const at = hovered ? { x: hovered.position.x, y: 0, z: hovered.position.z } : hoverCell;
  return inspectAt(
    { x: at.x, y: 0, z: at.z },
    { solids, zones, cellSize: tile, includeHidden: isGm, detail: hoverDetail },
  );
}

function InspectBar({ panel, map }) {
  const { hoverInfo, hoverDetail } = map.prefs;
  const { deploying, hoverCell, solids, tile } = map;
  const { terrain, isGm } = panel;
  const inspected = useMemo(() => {
    if (!hoverInfo || deploying || !hoverCell) return NOTHING_HERE;
    return inspectedAt(hoverCell, { solids, zones: terrain?.zones, tile, isGm, hoverDetail });
  }, [hoverInfo, hoverDetail, deploying, hoverCell, solids, terrain, tile, isGm]);
  if (inspected.length === 0) return null;
  return (
    <div className={styles.inspectBar} data-testid="battlefield-inspect">
      {inspected.map((line) => (
        <span key={line.title} className={styles.inspectEntry}>
          <b className={styles.inspectTitle}>{line.title}</b>
          <span className={styles.inspectFacts}>{line.facts.join(' · ')}</span>
          {line.meaning.length > 0 && <span className={styles.inspectMeaning}>{line.meaning.join(' · ')}</span>}
          {line.detail.length > 0 && (
            <span className={styles.inspectDetail} data-testid="inspect-detail">
              {line.detail.join(' · ')}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

/** The marker's own terrain switch on the readout: flips the token at the tape's far end. */
function targetTerrainToggle(token, writeToken) {
  if (!token) return undefined;
  return (value) => writeToken({ ...token, ignoresTerrain: value, rev: token.rev + 1 }, { immediate: true });
}

/**
 * The tape's answer when it has one, the live line otherwise. Suppressing
 * the live line whenever a `line` merely EXISTS left the bar showing an
 * unhelpful hint after every marker drag, because a drag leaves a line
 * behind with no measurement. NOT while building: the live line is combat
 * information and the rail underneath it is terrain.
 */
function MeasureReadout({ panel, map }) {
  const { line, setApplied } = map;
  const { liveMeasurement, coverPinned } = panel;
  const showsLive = liveMeasurement && !map.building;
  if (!line && !showsLive) return null;
  const measurement = line?.measurement || liveMeasurement;
  const onApply = () => {
    if (!measurement) return;
    panel.onApplyMeasurement?.(measurement);
    setApplied(true);
  };
  return (
    <BattlefieldReadout
      measurement={measurement}
      live={!line?.measurement && !!liveMeasurement}
      canApply={panel.canApplyDerived && !!measurement}
      applied={map.applied}
      pinned={coverPinned}
      onUnpin={coverPinned ? panel.onUnpinCover : undefined}
      targetName={panel.targetName}
      ignoringTerrain={panel.ignoreTerrain || line?.ignoringTerrain}
      targetIgnoresTerrain={line?.to?.token?.ignoresTerrain}
      onToggleTargetTerrain={targetTerrainToggle(line?.to?.token, panel.writeToken)}
      onApply={onApply}
    />
  );
}

/** Why a gun reads as it does, in a few words: what covers or hides him, or why the terrain does not count. */
function whyOf(r) {
  if (r.level === 'out') return r.source ? `the ${r.source} hides him` : 'he cannot be seen';
  if (r.source) return `the ${r.source}`;
  if (r.beyond)
    return `${r.beyond.source ? `the ${r.beyond.source}` : 'the terrain'} is ${r.beyond.metres.toFixed(1)} m from him: no cover`;
  return 'nothing between them';
}

/**
 * THE TAKE-COVER CUE'S READOUT (2026-10-25): off the map, never over the ghost (the user's objection to a label on
 * the map) - where the ghost has settled, or the reach tile under the pointer: the pace and distance, the worst
 * cover, then gun by gun with why. A live region, politely: it changes as the ghost moves.
 */
function CoverCueReadout({ reading, carrying, advancing }) {
  if (!reading) return null;
  return (
    <div className={styles.coverReadout} data-testid="cover-readout" role="status" aria-live="polite">
      <span className={styles.coverReadoutHead}>
        {carrying ? 'If he moves here' : 'There'}
        {reading.pace ? ` · ${reading.pace}` : ''}
        {' · reads as '}
        <span className={styles.coverReadoutLevel} data-level={reading.worst} data-testid="cover-readout-worst">
          {levelWord(reading.worst, { capital: true })}
        </span>
      </span>
      {advancing && (
        <span className={styles.coverReadoutRow} data-testid="cover-readout-advance">
          Tactical Advance: he counts as in cover while he makes this move.
        </span>
      )}
      {reading.readings.map((r) => (
        <span key={r.threatId} className={styles.coverReadoutRow} data-testid={`cover-readout-${r.threatId}`}>
          <span className={styles.coverReadoutLevel} data-level={r.level}>
            {levelWord(r.level, { capital: true })}
          </span>
          {` · ${r.name} - ${whyOf(r)}`}
        </span>
      ))}
    </div>
  );
}

/** The legend's key: only what is actually ON this map. */
function PanelLegend({ panel, map }) {
  const { solids } = map;
  const { isGm } = panel;
  const key = useMemo(() => legendKeyOf(solids, isGm), [solids, isGm]);
  return (
    <BattlefieldLegend
      mode={map.mode}
      materials={key.materials}
      terrainKinds={key.terrainKinds}
      climb={map.legendClimb}
    />
  );
}

/** The stage's name, narration and curtain, in Scenes mode. */
function StageBar({ scenes, stage }) {
  // The scene on stage has a record only once it has been named or cut to; a bare stage has nowhere to keep a phase.
  const onStage = scenes.scenes?.find((scene) => scene.id === stage?.sceneId) ?? null;
  return (
    <BattlefieldSceneBar
      name={stage?.name ?? ''}
      narration={stage?.narration ?? ''}
      curtain={!!stage?.curtain}
      phase={onStage?.phase ?? null}
      onText={scenes.setStageText}
      onCurtain={scenes.setCurtain}
      onPhase={onStage ? scenes.setScenePhase : undefined}
    />
  );
}

/** The instruments with the camera on the screen: the one part of the bottom layer that turns with it (8f). */
function CompassInstruments({ cameraStore, label, children }) {
  return (
    <DisplayInstruments camera={useCamera(cameraStore)} label={label}>
      {children}
    </DisplayInstruments>
  );
}

/**
 * The display's instruments, right-aligned above the readout and the legend: the compass, the last turn, the camera
 * label and Follow the action. Placed by coordinates, the tape's readout covered them on a small screen and the
 * follow looked broken, its one way back hidden (2026-10-20).
 */
function MapInstruments({ panel, map }) {
  // One element per last turn, so the instruments' memo holds across the panel's other renders.
  const scrub = useMemo(() => <LastTurnScrub lastTurn={panel.lastTurn} />, [panel.lastTurn]);
  if (map.renderer !== 'display') return null;
  return (
    <div className={displayStyles.instruments}>
      <CompassInstruments cameraStore={map.cameraStore} label={map.cameraLabel}>
        {scrub}
      </CompassInstruments>
      {map.wheelTaken && (
        <button type="button" className={displayStyles.follow} data-testid="display-follow" onClick={map.follow}>
          Follow the action
        </button>
      )}
    </div>
  );
}

/** The deploy marker's settings: shape, height, ring, team, redeploy. */
function DeployBar({ panel, map }) {
  const { roster, deployment, onStampTeam } = panel;
  const { deployment_, boundary } = map;
  const teams = useMemo(() => teamsIn(roster), [roster]);
  const capacityWarning = useMemo(() => capacityWarningOf(deployment, boundary), [deployment, boundary]);
  return (
    <BattlefieldDeployBar
      marker={deployment}
      shape={map.formationShape}
      onShape={map.setFormationShape}
      onAir={deployment_.setAir}
      onRing={deployment_.setRing}
      teams={teams}
      onTeam={onStampTeam ? deployment_.setTeam : undefined}
      arrivals={panel.arrivals}
      plantFor={map.plantFor}
      onPlantFor={panel.onSetArrivals ? map.setPlantFor : undefined}
      onRemoveArrival={deployment_.removeArrival}
      onRedeploy={() => deployment_.deployThese(deployment_.deployedTokens)}
      capacityWarning={capacityWarning}
      redeployOnMapLoad={panel.redeployOnMapLoad}
      onRedeployOnMapLoad={panel.onRedeployOnMapLoad}
    />
  );
}

/**
 * ONE LAYER FOR EVERYTHING ANCHORED TO THE BOTTOM. Each of these used to
 * place itself with its own `bottom:` and they fought over the corner: the
 * measure bar wraps upward as it fills and buried the readout above it.
 * Two boxes cannot be kept apart by choosing coordinates - in one flow
 * column nothing has to guess anybody else's height.
 *
 * The scene bar and the deploy bar are IN THE COLUMN, not floating over
 * it: anchored with a `bottom:` of their own, the deploy bar sat on top of
 * the readout (v8.23.1). So are the display's instruments, for the same
 * reason (v11.108.6).
 */
export default function PanelBottomLayer({ panel, map }) {
  const { scenes } = panel;
  return (
    <div className={styles.bottomLayer}>
      <MapInstruments panel={panel} map={map} />
      <div className={styles.bottomRow}>
        <div className={styles.bottomStack}>
          <ClimbBar facts={map.climbFacts} />
          <CoverCueReadout
            reading={map.cover?.readout}
            carrying={!!map.cover?.ghost}
            advancing={!!map.cover?.advancing}
          />
          <InspectBar panel={panel} map={map} />
          <MeasureReadout panel={panel} map={map} />
        </div>
        <PanelLegend panel={panel} map={map} />
      </div>
      {map.scenesMode && scenes && <StageBar scenes={scenes} stage={panel.stage} />}
      {map.deploying && <DeployBar panel={panel} map={map} />}
    </div>
  );
}
